import React from 'react';
import { TrendingUp, TrendingDown, Pause, ShieldCheck, AlertTriangle } from 'lucide-react';
import { recLabel, officialTooltip } from '../../lib/recLabel';

const fmtPrice = (v) => {
    if (v === null || v === undefined) return '—';
    if (v >= 1000) return Number(v).toLocaleString('en-US', { maximumFractionDigits: 2 });
    if (v >= 1) return Number(v).toLocaleString('en-US', { maximumFractionDigits: 4 });
    return Number(v).toLocaleString('en-US', { maximumFractionDigits: 8 });
};

const cfg = {
    LONG: {
        Icon: TrendingUp,
        wrap: 'border-emerald-400/40 bg-emerald-400/10',
        text: 'text-emerald-300',
        textStrong: 'text-emerald-400',
        bar: 'bg-emerald-400',
        accent: 'emerald-400',
    },
    SHORT: {
        Icon: TrendingDown,
        wrap: 'border-rose-500/40 bg-rose-500/10',
        text: 'text-rose-300',
        textStrong: 'text-rose-400',
        bar: 'bg-rose-500',
        accent: 'rose-500',
    },
    WAIT: {
        Icon: Pause,
        wrap: 'border-slate-600 bg-slate-700/40',
        text: 'text-slate-200',
        textStrong: 'text-slate-200',
        bar: 'bg-slate-400',
        accent: 'zinc-500',
    },
};

export function RecommendationBadge({ rec, compact = true, testid, secondary = false }) {
    if (!rec) return <span className="text-slate-500">—</span>;
    const c = cfg[rec.action] || cfg.WAIT;
    const Icon = c.Icon;
    const lbl = recLabel(rec);

    if (compact) {
        return (
            <div
                data-testid={testid || `rec-${rec.action}`}
                data-official={lbl.official ? 'true' : 'false'}
                className={`inline-flex items-center gap-1.5 border rounded-sm px-2 py-1 cursor-help ${lbl.weak ? cfg.WAIT.wrap : c.wrap}`}
                title={`${officialTooltip(rec)}${rec.summary ? ` — ${rec.summary}` : ''}`}
            >
                <Icon className={`h-3 w-3 ${lbl.weak ? 'text-slate-400' : c.textStrong}`} />
                <span className={`text-[10px] font-bold uppercase tracking-wider ${lbl.weak ? 'text-slate-300' : c.textStrong}`}>{lbl.label}</span>
                {lbl.official && (
                    <span className="inline-flex items-center gap-0.5 text-[8px] font-bold px-1 py-px rounded-sm bg-emerald-500 text-slate-950">
                        <ShieldCheck className="h-2.5 w-2.5" /> RESMİ
                    </span>
                )}
                {lbl.weak && <span className="text-[8px] font-bold px-1 py-px rounded-sm bg-slate-600 text-slate-200">ZAYIF</span>}
                {lbl.official && (
                    <>
                        <span className={`text-[10px] font-mono tabular-nums ${c.textStrong}`}>+{rec.profit_pct}%</span>
                        {rec.leverage?.label && (
                            <span className="text-[9px] font-mono font-bold px-1 py-px rounded-sm bg-slate-600/80 text-slate-100 border border-slate-600/50">
                                {rec.leverage.label}
                            </span>
                        )}
                    </>
                )}
            </div>
        );
    }

    return (
        <div className={`border rounded-md p-4 ${c.wrap}`} data-testid={testid || `rec-card-${rec.action}`}>
            {rec.action !== 'WAIT' && (
                <div
                    data-testid="rec-official-banner"
                    className={`mb-3 text-[11px] leading-snug rounded-sm px-2 py-1.5 border flex items-start gap-1.5 ${
                        lbl.official ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-100' : 'bg-amber-500/10 border-amber-500/40 text-amber-100'
                    }`}
                >
                    {lbl.official ? <ShieldCheck className="h-3.5 w-3.5 shrink-0 mt-px" /> : <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-px" />}
                    <span>{lbl.official ? 'RESMİ SİNYAL — istatistiğe ve Telegram bildirimlerine giren, işlem açılabilir sinyal.' : `RESMİ DEĞİL — sadece eğilim, işlem açma önerilmez. Eksik: ${(rec.official_missing || []).join(' · ')}`}</span>
                </div>
            )}
            {rec.regime_note_tr && (
                <div data-testid="rec-regime-note" className="mb-3 text-[11px] leading-snug rounded-sm px-2 py-1.5 border border-rose-500/40 bg-rose-500/10 text-rose-100">
                    {rec.regime_note_tr}
                </div>
            )}
            {rec.late && (
                <div data-testid="rec-late-note" className="mb-3 text-[11px] leading-snug rounded-sm px-2 py-1.5 border border-amber-400/50 bg-amber-400/10 text-amber-100">
                    🚂 <b>Tren kaçtı — kovalama yapma.</b> {(rec.late.reasons_tr || []).join(', ')}. Resmi sinyal verilmez, kaldıraç 1x;
                    {rec.late.pullback_level ? ` geri çekilmede ${rec.late.pullback_level} civarını izle (radar planı).` : ' geri çekilme veya yeni birikim bekle.'}
                </div>
            )}
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <div className={`h-9 w-9 rounded-sm border flex items-center justify-center ${c.wrap}`}>
                        <Icon className={`h-4 w-4 ${c.textStrong}`} />
                    </div>
                    <div>
                        <div className={`text-base font-bold tracking-tight ${c.textStrong}`}>
                            {lbl.label}
                            {rec.action !== 'WAIT' && rec.leverage?.label && !secondary && (
                                <span className="ml-2 text-[11px] font-mono font-bold px-1.5 py-0.5 rounded-sm bg-slate-600 text-slate-100 align-middle">
                                    {rec.leverage.label}
                                </span>
                            )}
                        </div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-400">Güven: {rec.confidence}%</div>
                    </div>
                </div>
                {rec.action !== 'WAIT' && (
                    <div className="text-right">
                        <div className="text-[10px] uppercase tracking-wider text-slate-400">Olası Kâr</div>
                        <div className={`text-2xl font-mono font-bold leading-none tracking-tight ${c.textStrong}`}>
                            +{rec.profit_pct}%
                        </div>
                    </div>
                )}
            </div>

            {rec.action !== 'WAIT' && !secondary && (
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 pt-3 border-t border-current/10">
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-400">Giriş</div>
                        <div className="text-xs font-mono text-slate-100 mt-1">${fmtPrice(rec.entry)}</div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-emerald-400/80">TP1 (İlk Hedef)</div>
                        <div className={`text-xs font-mono ${c.textStrong} mt-1`}>${fmtPrice(rec.tp1)}</div>
                        <div className="text-[10px] font-mono text-slate-400">+{rec.tp1_pct}%</div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-emerald-400/80">TP2 (Ana Hedef)</div>
                        <div className={`text-xs font-mono ${c.textStrong} mt-1`}>${fmtPrice(rec.tp2 ?? rec.target)}</div>
                        <div className="text-[10px] font-mono text-slate-400">+{rec.tp2_pct ?? rec.profit_pct}%</div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-rose-400/80">Stop Loss</div>
                        <div className="text-xs font-mono text-rose-400 mt-1">${fmtPrice(rec.stop_loss)}</div>
                        <div className="text-[10px] font-mono text-slate-400">-{rec.loss_pct}%</div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-400">R/R</div>
                        <div className={`text-xs font-mono mt-1 ${rec.rr_ratio >= 1.5 ? c.textStrong : 'text-amber-400'}`}>
                            {rec.rr_ratio}:1
                        </div>
                    </div>
                </div>
            )}

            {rec.action !== 'WAIT' && rec.plain_summary && (
                <div className="pt-3 border-t border-current/10 mt-3">
                    <div className="text-[11px] text-slate-200 leading-relaxed font-medium" data-testid="rec-plain-summary">
                        {rec.plain_summary}
                    </div>
                    {rec.probability_tp1 !== undefined && (
                        <div className="mt-2 flex items-center gap-2 text-[10px] font-mono">
                            <div className="flex-1 flex rounded-sm overflow-hidden h-2 bg-slate-700">
                                <div
                                    className="bg-emerald-500/70"
                                    style={{ width: `${rec.probability_tp1}%` }}
                                    title={`TP1 ihtimali %${rec.probability_tp1}`}
                                />
                                <div
                                    className="bg-rose-500/60"
                                    style={{ width: `${rec.probability_sl}%` }}
                                    title={`SL ihtimali %${rec.probability_sl}`}
                                />
                                <div
                                    className="bg-slate-500"
                                    style={{ width: `${Math.max(0, 100 - rec.probability_tp1 - rec.probability_sl)}%` }}
                                    title="belirsiz / süre dolacak"
                                />
                            </div>
                            <span className="text-emerald-400 w-8 text-right">%{rec.probability_tp1}</span>
                            <span className="text-slate-400">·</span>
                            <span className="text-rose-400 w-8 text-right">%{rec.probability_sl}</span>
                        </div>
                    )}
                    {rec.expected_timing && (
                        <div className="mt-1.5 text-[10px] text-slate-400">
                            Tahmini zamanlama: <span className="text-slate-200">{rec.expected_timing}</span>
                        </div>
                    )}
                </div>
            )}

            {rec.action !== 'WAIT' && !secondary && (
                <div className="text-[10px] text-slate-400 mt-3 pt-3 border-t border-current/10 leading-relaxed">
                    <span className="text-slate-300 font-medium">İşlem planı:</span> ${fmtPrice(rec.entry)} civarında {rec.action === 'LONG' ? 'long' : 'short'} aç,
                    pozisyonun yarısını ${fmtPrice(rec.tp1)} (TP1) seviyesinde kapat, kalanı ${fmtPrice(rec.tp2 ?? rec.target)} (TP2) hedefine taşı.
                    Stop loss: ${fmtPrice(rec.stop_loss)}.
                </div>
            )}

            {rec.leverage?.warning && rec.action !== 'WAIT' && !secondary && (
                <div className="text-[10px] text-amber-400/90 mt-2 leading-snug">
                    ⚠ {rec.leverage.warning}
                </div>
            )}

            {rec.summary && (
                <div className="text-[11px] text-slate-200 mt-2 leading-snug font-medium" data-testid="rec-summary">
                    {rec.summary}
                </div>
            )}

            <div className="text-[10px] text-slate-400 mt-2 leading-relaxed">{rec.reason}</div>
        </div>
    );
}
